/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { InvestmentEntryType } from './InvestmentEntryType';
import {
    InvestmentEntryTypeFromJSON,
    InvestmentEntryTypeFromJSONTyped,
    InvestmentEntryTypeToJSON,
} from './InvestmentEntryType';
import type { AdminProductDetail } from './AdminProductDetail';
import {
    AdminProductDetailFromJSON,
    AdminProductDetailFromJSONTyped,
    AdminProductDetailToJSON,
} from './AdminProductDetail';
import type { WalletTypeEnum } from './WalletTypeEnum';
import {
    WalletTypeEnumFromJSON,
    WalletTypeEnumFromJSONTyped,
    WalletTypeEnumToJSON,
} from './WalletTypeEnum';
import type { SurveyWaiverTypeEnum } from './SurveyWaiverTypeEnum';
import {
    SurveyWaiverTypeEnumFromJSON,
    SurveyWaiverTypeEnumFromJSONTyped,
    SurveyWaiverTypeEnumToJSON,
} from './SurveyWaiverTypeEnum';
import type { Wallet } from './Wallet';
import {
    WalletFromJSON,
    WalletFromJSONTyped,
    WalletToJSON,
} from './Wallet';
import type { InvestmentCompliance } from './InvestmentCompliance';
import {
    InvestmentComplianceFromJSON,
    InvestmentComplianceFromJSONTyped,
    InvestmentComplianceToJSON,
} from './InvestmentCompliance';
import type { InvestorCompliance } from './InvestorCompliance';
import {
    InvestorComplianceFromJSON,
    InvestorComplianceFromJSONTyped,
    InvestorComplianceToJSON,
} from './InvestorCompliance';
import type { Money } from './Money';
import {
    MoneyFromJSON,
    MoneyFromJSONTyped,
    MoneyToJSON,
} from './Money';
import type { StepEnum } from './StepEnum';
import {
    StepEnumFromJSON,
    StepEnumFromJSONTyped,
    StepEnumToJSON,
} from './StepEnum';
import type { InvestmentCancellationEnum } from './InvestmentCancellationEnum';
import {
    InvestmentCancellationEnumFromJSON,
    InvestmentCancellationEnumFromJSONTyped,
    InvestmentCancellationEnumToJSON,
} from './InvestmentCancellationEnum';
import type { KycStatusEnum } from './KycStatusEnum';
import {
    KycStatusEnumFromJSON,
    KycStatusEnumFromJSONTyped,
    KycStatusEnumToJSON,
} from './KycStatusEnum';
import type { AdminInvestmentVoucher } from './AdminInvestmentVoucher';
import {
    AdminInvestmentVoucherFromJSON,
    AdminInvestmentVoucherFromJSONTyped,
    AdminInvestmentVoucherToJSON,
} from './AdminInvestmentVoucher';
import type { CancellationImpedimentsEnum } from './CancellationImpedimentsEnum';
import {
    CancellationImpedimentsEnumFromJSON,
    CancellationImpedimentsEnumFromJSONTyped,
    CancellationImpedimentsEnumToJSON,
} from './CancellationImpedimentsEnum';
import type { AdminMinimalInvestor } from './AdminMinimalInvestor';
import {
    AdminMinimalInvestorFromJSON,
    AdminMinimalInvestorFromJSONTyped,
    AdminMinimalInvestorToJSON,
} from './AdminMinimalInvestor';
import type { FrontendStatusEnum } from './FrontendStatusEnum';
import {
    FrontendStatusEnumFromJSON,
    FrontendStatusEnumFromJSONTyped,
    FrontendStatusEnumToJSON,
} from './FrontendStatusEnum';
import type { InvestmentAcquisitionTypeRecipientEnum } from './InvestmentAcquisitionTypeRecipientEnum';
import {
    InvestmentAcquisitionTypeRecipientEnumFromJSON,
    InvestmentAcquisitionTypeRecipientEnumFromJSONTyped,
    InvestmentAcquisitionTypeRecipientEnumToJSON,
} from './InvestmentAcquisitionTypeRecipientEnum';
import type { TokenizationApiOperationStatus } from './TokenizationApiOperationStatus';
import {
    TokenizationApiOperationStatusFromJSON,
    TokenizationApiOperationStatusFromJSONTyped,
    TokenizationApiOperationStatusToJSON,
} from './TokenizationApiOperationStatus';

/**
 * Extends `core.serializers.investment.NoDocumentsInvestmentSerializer` for
 * the Admin Dashboard.
 * @export
 * @interface AdminInvestmentDetails
 */
export interface AdminInvestmentDetails {
    /**
     * the date the issuer accepted the investment offer.
     * @type {Date}
     * @memberof AdminInvestmentDetails
     */
    readonly acceptedAt: Date | null;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvestmentDetails
     */
    readonly accruedInterest: Money;
    /**
     * 
     * @type {InvestmentAcquisitionTypeRecipientEnum}
     * @memberof AdminInvestmentDetails
     */
    readonly acquisitionType: InvestmentAcquisitionTypeRecipientEnum;
    /**
     * 
     * @type {WalletTypeEnum}
     * @memberof AdminInvestmentDetails
     */
    addWalletLater: WalletTypeEnum | null;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvestmentDetails
     */
    readonly agio: Money | null;
    /**
     * This is only information if the Investment can be aborted in principle. One still needs to check if the requesting user has permission to do so.
     * @type {boolean}
     * @memberof AdminInvestmentDetails
     */
    readonly canAbort: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof AdminInvestmentDetails
     */
    readonly canAccept: boolean;
    /**
     * This is only information if the Investment can be cancelled in principle. One still needs to check if the requesting user has permission to do so.
     * @type {Array<InvestmentCancellationEnum>}
     * @memberof AdminInvestmentDetails
     */
    readonly canCancel: Array<InvestmentCancellationEnum>;
    /**
     * This is only information if the Investment can be issued in principle. One still needs to check if the requesting user has permission to do so.
     * @type {boolean}
     * @memberof AdminInvestmentDetails
     */
    readonly canIssue: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof AdminInvestmentDetails
     */
    readonly canReceivePayment: boolean;
    /**
     * the date the issuer, admin or investor canceled the investment offer
     * @type {Date}
     * @memberof AdminInvestmentDetails
     */
    readonly cancellationDate: Date | null;
    /**
     * 
     * @type {Date}
     * @memberof AdminInvestmentDetails
     */
    readonly cancellationDeadline: Date | null;
    /**
     * 
     * @type {Array<CancellationImpedimentsEnum>}
     * @memberof AdminInvestmentDetails
     */
    readonly cancellationImpediments: Array<CancellationImpedimentsEnum>;
    /**
     * reason why investment was canceled / aborted
     * @type {InvestmentCancellationEnum}
     * @memberof AdminInvestmentDetails
     */
    readonly cancellationReason: InvestmentCancellationEnum | null;
    /**
     * 
     * @type {boolean}
     * @memberof AdminInvestmentDetails
     */
    readonly complianceExportRequired: boolean;
    /**
     * 
     * @type {Date}
     * @memberof AdminInvestmentDetails
     */
    readonly considerationStartedAt: Date;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvestmentDetails
     */
    readonly disagio: Money | null;
    /**
     * 
     * @type {InvestmentEntryType}
     * @memberof AdminInvestmentDetails
     */
    entryType?: InvestmentEntryType;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvestmentDetails
     */
    exemptionOrderAmount: Money | null;
    /**
     * Latest time when the exemption_order_amount was changed
     * @type {Date}
     * @memberof AdminInvestmentDetails
     */
    exemptionOrderDate?: Date | null;
    /**
     * 
     * @type {FrontendStatusEnum}
     * @memberof AdminInvestmentDetails
     */
    readonly frontendStatus: FrontendStatusEnum;
    /**
     * 
     * @type {number}
     * @memberof AdminInvestmentDetails
     */
    fulfilledAmount: number;
    /**
     * 
     * @type {string}
     * @memberof AdminInvestmentDetails
     */
    readonly id: string;
    /**
     * whether an identification was created by this investment
     * @type {boolean}
     * @memberof AdminInvestmentDetails
     */
    readonly initiatedIdentification: boolean;
    /**
     * 
     * @type {InvestmentCompliance}
     * @memberof AdminInvestmentDetails
     */
    readonly investmentCompliance: InvestmentCompliance | null;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvestmentDetails
     */
    readonly investmentTotal: Money;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvestmentDetails
     */
    readonly investmentTotalFulfilled: Money;
    /**
     * 
     * @type {AdminMinimalInvestor}
     * @memberof AdminInvestmentDetails
     */
    readonly investor: AdminMinimalInvestor;
    /**
     * 
     * @type {InvestorCompliance}
     * @memberof AdminInvestmentDetails
     */
    readonly investorCompliance: InvestorCompliance | null;
    /**
     * 
     * @type {boolean}
     * @memberof AdminInvestmentDetails
     */
    readonly isEcsp: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof AdminInvestmentDetails
     */
    readonly isTokenized: boolean;
    /**
     * 
     * @type {TokenizationApiOperationStatus}
     * @memberof AdminInvestmentDetails
     */
    readonly issuanceStatus: TokenizationApiOperationStatus | null;
    /**
     * 
     * @type {KycStatusEnum}
     * @memberof AdminInvestmentDetails
     */
    readonly kycStatus: KycStatusEnum | null;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvestmentDetails
     */
    readonly lastSettledPayout: Money | null;
    /**
     * 
     * @type {number}
     * @memberof AdminInvestmentDetails
     */
    maxAmount: number;
    /**
     * 
     * @type {Date}
     * @memberof AdminInvestmentDetails
     */
    readonly nextScheduledPayout: Date | null;
    /**
     * 
     * @type {Date}
     * @memberof AdminInvestmentDetails
     */
    paidAt: Date | null;
    /**
     * bank transfer text for associating the payment
     * @type {string}
     * @memberof AdminInvestmentDetails
     */
    readonly paymentCode: string;
    /**
     * 
     * @type {Date}
     * @memberof AdminInvestmentDetails
     */
    readonly paymentDeadline: Date | null;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvestmentDetails
     */
    readonly paymentTotal: Money;
    /**
     * 
     * @type {string}
     * @memberof AdminInvestmentDetails
     */
    productName: string;
    /**
     * 
     * @type {boolean}
     * @memberof AdminInvestmentDetails
     */
    readonly redeemed: boolean;
    /**
     * 
     * @type {number}
     * @memberof AdminInvestmentDetails
     */
    remainingAmount: number;
    /**
     * the date the investor made the investment offer.
     * @type {Date}
     * @memberof AdminInvestmentDetails
     */
    readonly signedAt: Date;
    /**
     * 
     * @type {StepEnum}
     * @memberof AdminInvestmentDetails
     */
    readonly step: StepEnum;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvestmentDetails
     */
    readonly sumSettledPayouts: Money;
    /**
     * survey was valid at submission time and thus no waiver is required
     * @type {boolean}
     * @memberof AdminInvestmentDetails
     */
    readonly surveyValid: boolean;
    /**
     * 
     * @type {SurveyWaiverTypeEnum}
     * @memberof AdminInvestmentDetails
     */
    readonly surveyWaiverType: SurveyWaiverTypeEnum;
    /**
     * 
     * @type {AdminProductDetail}
     * @memberof AdminInvestmentDetails
     */
    readonly token: AdminProductDetail;
    /**
     * 
     * @type {Date}
     * @memberof AdminInvestmentDetails
     */
    readonly tokenDeliveredAt: Date | null;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvestmentDetails
     */
    readonly tokenPrice: Money;
    /**
     * transaction hash in the blockchain. tokens are considered issued once they are on the blockchain.
     * @type {string}
     * @memberof AdminInvestmentDetails
     */
    readonly tokenTransactionHash: string;
    /**
     * 
     * @type {boolean}
     * @memberof AdminInvestmentDetails
     */
    readonly usesEffecta: boolean;
    /**
     * 
     * @type {AdminInvestmentVoucher}
     * @memberof AdminInvestmentDetails
     */
    readonly voucher: AdminInvestmentVoucher | null;
    /**
     * 
     * @type {Wallet}
     * @memberof AdminInvestmentDetails
     */
    wallet: Wallet | null;
}



/**
 * Check if a given object implements the AdminInvestmentDetails interface.
 */
export function instanceOfAdminInvestmentDetails(value: object): value is AdminInvestmentDetails {
    if (!('acceptedAt' in value) || value['acceptedAt'] === undefined) return false;
    if (!('accruedInterest' in value) || value['accruedInterest'] === undefined) return false;
    if (!('acquisitionType' in value) || value['acquisitionType'] === undefined) return false;
    if (!('addWalletLater' in value) || value['addWalletLater'] === undefined) return false;
    if (!('agio' in value) || value['agio'] === undefined) return false;
    if (!('canAbort' in value) || value['canAbort'] === undefined) return false;
    if (!('canAccept' in value) || value['canAccept'] === undefined) return false;
    if (!('canCancel' in value) || value['canCancel'] === undefined) return false;
    if (!('canIssue' in value) || value['canIssue'] === undefined) return false;
    if (!('canReceivePayment' in value) || value['canReceivePayment'] === undefined) return false;
    if (!('cancellationDate' in value) || value['cancellationDate'] === undefined) return false;
    if (!('cancellationDeadline' in value) || value['cancellationDeadline'] === undefined) return false;
    if (!('cancellationImpediments' in value) || value['cancellationImpediments'] === undefined) return false;
    if (!('cancellationReason' in value) || value['cancellationReason'] === undefined) return false;
    if (!('complianceExportRequired' in value) || value['complianceExportRequired'] === undefined) return false;
    if (!('considerationStartedAt' in value) || value['considerationStartedAt'] === undefined) return false;
    if (!('disagio' in value) || value['disagio'] === undefined) return false;
    if (!('exemptionOrderAmount' in value) || value['exemptionOrderAmount'] === undefined) return false;
    if (!('frontendStatus' in value) || value['frontendStatus'] === undefined) return false;
    if (!('fulfilledAmount' in value) || value['fulfilledAmount'] === undefined) return false;
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('initiatedIdentification' in value) || value['initiatedIdentification'] === undefined) return false;
    if (!('investmentCompliance' in value) || value['investmentCompliance'] === undefined) return false;
    if (!('investmentTotal' in value) || value['investmentTotal'] === undefined) return false;
    if (!('investmentTotalFulfilled' in value) || value['investmentTotalFulfilled'] === undefined) return false;
    if (!('investor' in value) || value['investor'] === undefined) return false;
    if (!('investorCompliance' in value) || value['investorCompliance'] === undefined) return false;
    if (!('isEcsp' in value) || value['isEcsp'] === undefined) return false;
    if (!('isTokenized' in value) || value['isTokenized'] === undefined) return false;
    if (!('issuanceStatus' in value) || value['issuanceStatus'] === undefined) return false;
    if (!('kycStatus' in value) || value['kycStatus'] === undefined) return false;
    if (!('lastSettledPayout' in value) || value['lastSettledPayout'] === undefined) return false;
    if (!('maxAmount' in value) || value['maxAmount'] === undefined) return false;
    if (!('nextScheduledPayout' in value) || value['nextScheduledPayout'] === undefined) return false;
    if (!('paidAt' in value) || value['paidAt'] === undefined) return false;
    if (!('paymentCode' in value) || value['paymentCode'] === undefined) return false;
    if (!('paymentDeadline' in value) || value['paymentDeadline'] === undefined) return false;
    if (!('paymentTotal' in value) || value['paymentTotal'] === undefined) return false;
    if (!('productName' in value) || value['productName'] === undefined) return false;
    if (!('redeemed' in value) || value['redeemed'] === undefined) return false;
    if (!('remainingAmount' in value) || value['remainingAmount'] === undefined) return false;
    if (!('signedAt' in value) || value['signedAt'] === undefined) return false;
    if (!('step' in value) || value['step'] === undefined) return false;
    if (!('sumSettledPayouts' in value) || value['sumSettledPayouts'] === undefined) return false;
    if (!('surveyValid' in value) || value['surveyValid'] === undefined) return false;
    if (!('surveyWaiverType' in value) || value['surveyWaiverType'] === undefined) return false;
    if (!('token' in value) || value['token'] === undefined) return false;
    if (!('tokenDeliveredAt' in value) || value['tokenDeliveredAt'] === undefined) return false;
    if (!('tokenPrice' in value) || value['tokenPrice'] === undefined) return false;
    if (!('tokenTransactionHash' in value) || value['tokenTransactionHash'] === undefined) return false;
    if (!('usesEffecta' in value) || value['usesEffecta'] === undefined) return false;
    if (!('voucher' in value) || value['voucher'] === undefined) return false;
    if (!('wallet' in value) || value['wallet'] === undefined) return false;
    return true;
}

export function AdminInvestmentDetailsFromJSON(json: any): AdminInvestmentDetails {
    return AdminInvestmentDetailsFromJSONTyped(json, false);
}

export function AdminInvestmentDetailsFromJSONTyped(json: any, ignoreDiscriminator: boolean): AdminInvestmentDetails {
    if (json == null) {
        return json;
    }
    return {
        
        'acceptedAt': (json['accepted_at'] == null ? null : new Date(json['accepted_at'])),
        'accruedInterest': MoneyFromJSON(json['accrued_interest']),
        'acquisitionType': InvestmentAcquisitionTypeRecipientEnumFromJSON(json['acquisition_type']),
        'addWalletLater': WalletTypeEnumFromJSON(json['add_wallet_later']),
        'agio': MoneyFromJSON(json['agio']),
        'canAbort': json['can_abort'],
        'canAccept': json['can_accept'],
        'canCancel': ((json['can_cancel'] as Array<any>).map(InvestmentCancellationEnumFromJSON)),
        'canIssue': json['can_issue'],
        'canReceivePayment': json['can_receive_payment'],
        'cancellationDate': (json['cancellation_date'] == null ? null : new Date(json['cancellation_date'])),
        'cancellationDeadline': (json['cancellation_deadline'] == null ? null : new Date(json['cancellation_deadline'])),
        'cancellationImpediments': ((json['cancellation_impediments'] as Array<any>).map(CancellationImpedimentsEnumFromJSON)),
        'cancellationReason': InvestmentCancellationEnumFromJSON(json['cancellation_reason']),
        'complianceExportRequired': json['compliance_export_required'],
        'considerationStartedAt': (new Date(json['consideration_started_at'])),
        'disagio': MoneyFromJSON(json['disagio']),
        'entryType': json['entry_type'] == null ? undefined : InvestmentEntryTypeFromJSON(json['entry_type']),
        'exemptionOrderAmount': MoneyFromJSON(json['exemption_order_amount']),
        'exemptionOrderDate': json['exemption_order_date'] == null ? undefined : (new Date(json['exemption_order_date'])),
        'frontendStatus': FrontendStatusEnumFromJSON(json['frontend_status']),
        'fulfilledAmount': json['fulfilled_amount'],
        'id': json['id'],
        'initiatedIdentification': json['initiated_identification'],
        'investmentCompliance': InvestmentComplianceFromJSON(json['investment_compliance']),
        'investmentTotal': MoneyFromJSON(json['investment_total']),
        'investmentTotalFulfilled': MoneyFromJSON(json['investment_total_fulfilled']),
        'investor': AdminMinimalInvestorFromJSON(json['investor']),
        'investorCompliance': InvestorComplianceFromJSON(json['investor_compliance']),
        'isEcsp': json['is_ecsp'],
        'isTokenized': json['is_tokenized'],
        'issuanceStatus': TokenizationApiOperationStatusFromJSON(json['issuance_status']),
        'kycStatus': KycStatusEnumFromJSON(json['kyc_status']),
        'lastSettledPayout': MoneyFromJSON(json['last_settled_payout']),
        'maxAmount': json['max_amount'],
        'nextScheduledPayout': (json['next_scheduled_payout'] == null ? null : new Date(json['next_scheduled_payout'])),
        'paidAt': (json['paid_at'] == null ? null : new Date(json['paid_at'])),
        'paymentCode': json['payment_code'],
        'paymentDeadline': (json['payment_deadline'] == null ? null : new Date(json['payment_deadline'])),
        'paymentTotal': MoneyFromJSON(json['payment_total']),
        'productName': json['product_name'],
        'redeemed': json['redeemed'],
        'remainingAmount': json['remaining_amount'],
        'signedAt': (new Date(json['signed_at'])),
        'step': StepEnumFromJSON(json['step']),
        'sumSettledPayouts': MoneyFromJSON(json['sum_settled_payouts']),
        'surveyValid': json['survey_valid'],
        'surveyWaiverType': SurveyWaiverTypeEnumFromJSON(json['survey_waiver_type']),
        'token': AdminProductDetailFromJSON(json['token']),
        'tokenDeliveredAt': (json['token_delivered_at'] == null ? null : new Date(json['token_delivered_at'])),
        'tokenPrice': MoneyFromJSON(json['token_price']),
        'tokenTransactionHash': json['token_transaction_hash'],
        'usesEffecta': json['uses_effecta'],
        'voucher': AdminInvestmentVoucherFromJSON(json['voucher']),
        'wallet': WalletFromJSON(json['wallet']),
    };
}

export function AdminInvestmentDetailsToJSON(value?: Omit<AdminInvestmentDetails, 'accepted_at'|'accrued_interest'|'acquisition_type'|'agio'|'can_abort'|'can_accept'|'can_cancel'|'can_issue'|'can_receive_payment'|'cancellation_date'|'cancellation_deadline'|'cancellation_impediments'|'cancellation_reason'|'compliance_export_required'|'consideration_started_at'|'disagio'|'frontend_status'|'id'|'initiated_identification'|'investment_compliance'|'investment_total'|'investment_total_fulfilled'|'investor'|'investor_compliance'|'is_ecsp'|'is_tokenized'|'issuance_status'|'kyc_status'|'last_settled_payout'|'next_scheduled_payout'|'payment_code'|'payment_deadline'|'payment_total'|'redeemed'|'signed_at'|'step'|'sum_settled_payouts'|'survey_valid'|'survey_waiver_type'|'token'|'token_delivered_at'|'token_price'|'token_transaction_hash'|'uses_effecta'|'voucher'> | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'add_wallet_later': WalletTypeEnumToJSON(value['addWalletLater']),
        'entry_type': InvestmentEntryTypeToJSON(value['entryType']),
        'exemption_order_amount': MoneyToJSON(value['exemptionOrderAmount']),
        'exemption_order_date': value['exemptionOrderDate'] == null ? undefined : ((value['exemptionOrderDate'] as any).toISOString()),
        'fulfilled_amount': value['fulfilledAmount'],
        'max_amount': value['maxAmount'],
        'paid_at': (value['paidAt'] == null ? null : (value['paidAt'] as any).toISOString()),
        'product_name': value['productName'],
        'remaining_amount': value['remainingAmount'],
        'wallet': WalletToJSON(value['wallet']),
    };
}

